import { Range, editor as MonacoEditor } from 'monaco-editor';
import {
  findRobotStartLineInCode,
  getInsertLineBeforeRobotStartInCode,
  getManagedRobotStartLineNumbersInCode,
  hasManagedRobotStartCallInCode,
  ROBOT_START_CALL_LINE_RE,
} from './robotStartCallUtils';
import {
  findRegionEndLineIndexAfterStart,
  findRegionSpanLineIndices,
  INPUT_MAPPINGS_REGION_START,
  isInputMappingsRegionStartLine,
  isRegionInteriorEmpty,
  REGION_END,
} from './programRegionMarkers';
import { findBalancedCallLineRanges, splitCodeLines } from './pythonSourceScanUtils';

export const ROBOT_START_COMMENT = '# Start your robot!';
export const ROBOT_BASE_IMPORT = 'from sphero import *';
const ROBOT_BASE_IMPORT_RE = /^\s*from\s+sphero\s+import\s+(?:\*|[^#\n]*\bRobotBase\b)/m;
const robotStartRemovalAllowance = new WeakSet<MonacoEditor.ITextModel>();

export function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Reads full program text from a Monaco model (or test doubles that only expose line APIs). */
function getCodeTextFromModel(model: MonacoEditor.ITextModel): string {
  if (typeof model.getValue === 'function') return model.getValue();
  const lineCount = model.getLineCount();
  const lines: string[] = [];
  for (let i = 1; i <= lineCount; i++) {
    lines.push(model.getLineContent(i));
  }
  return lines.join('\n');
}

function getLineModelFromCode(code: string): Pick<MonacoEditor.ITextModel, 'getLineCount' | 'getLineContent' | 'getLineMaxColumn'> {
  const lines = code.split('\n');
  return {
    getLineCount: () => lines.length,
    getLineContent: (lineNumber: number) => lines[lineNumber - 1] ?? '',
    getLineMaxColumn: (lineNumber: number) => (lines[lineNumber - 1] ?? '').length + 1,
  };
}

function positionToOffset(code: string, lineNumber: number, column: number): number {
  const lines = code.split('\n');
  const normalizedLine = Math.max(1, Math.min(lineNumber, lines.length));
  const normalizedColumn = Math.max(1, column);
  let offset = 0;

  for (let index = 0; index < normalizedLine - 1; index++) {
    offset += lines[index].length + 1;
  }

  const line = lines[normalizedLine - 1] ?? '';
  return offset + Math.min(normalizedColumn - 1, line.length);
}

function applyCodeEdits(code: string, edits: { range: Range; text: string }[]): string {
  return edits
    .slice()
    .sort(
      (left, right) =>
        positionToOffset(code, right.range.startLineNumber, right.range.startColumn) -
        positionToOffset(code, left.range.startLineNumber, left.range.startColumn)
    )
    .reduce((nextCode, edit) => {
      const start = positionToOffset(nextCode, edit.range.startLineNumber, edit.range.startColumn);
      const end = positionToOffset(nextCode, edit.range.endLineNumber, edit.range.endColumn);
      return `${nextCode.slice(0, start)}${edit.text}${nextCode.slice(end)}`;
    }, code);
}

export interface RobotStartCallRange {
  startLine: number;
  endLine: number;
}

interface RobotStartEdit {
  range: Range;
  text: string;
  forceMoveMarkers?: boolean;
}

/** Returns the 1-based line number of the .start() call, or null if not found. */
export function findRobotStartLine(model: MonacoEditor.ITextModel): number | null {
  return findRobotStartLineInCode(getCodeTextFromModel(model));
}

function getStartCallOpenParenIndex(line: string): number | null {
  const dotStartIndex = line.search(/\.\s*start\s*\(/);
  if (dotStartIndex < 0) return null;
  const openOffset = line.slice(dotStartIndex).indexOf('(');
  return openOffset < 0 ? null : dotStartIndex + openOffset;
}

function findStartCallRanges(model: MonacoEditor.ITextModel, matchesStartLine: (line: string, lineNumber: number) => boolean): RobotStartCallRange[] {
  const code = getCodeTextFromModel(model);
  const lines = splitCodeLines(code);
  return findBalancedCallLineRanges(lines, (line, lineIndex) => matchesStartLine(line, lineIndex + 1), getStartCallOpenParenIndex).map(
    ({ startLineIndex, endLineIndex }) => ({
      startLine: startLineIndex + 1,
      endLine: endLineIndex + 1,
    })
  );
}

export function findRobotStartCallRanges(model: MonacoEditor.ITextModel): RobotStartCallRange[] {
  return findStartCallRanges(model, (line) => ROBOT_START_CALL_LINE_RE.test(line));
}

export function findManagedRobotStartCallRanges(model: MonacoEditor.ITextModel): RobotStartCallRange[] {
  const managedStartLines = getManagedRobotStartLineNumbersInCode(getCodeTextFromModel(model));
  return findStartCallRanges(model, (_line, lineNumber) => managedStartLines.has(lineNumber));
}

export function hasRobotStartCall(code: string): boolean {
  const lines = code.split('\n');
  const model = {
    getLineCount: () => lines.length,
    getLineContent: (lineNumber: number) => lines[lineNumber - 1] ?? '',
  };

  return findRobotStartCallRanges(model as MonacoEditor.ITextModel).length > 0;
}

export function hasManagedRobotStartCall(code: string): boolean {
  return hasManagedRobotStartCallInCode(code);
}

export function hasRobotBaseImport(code: string): boolean {
  return ROBOT_BASE_IMPORT_RE.test(code);
}

function ensureRobotBaseImportInCode(code: string): string {
  if (hasRobotBaseImport(code)) return code;
  return code.trim().length === 0 ? `${ROBOT_BASE_IMPORT}\n\n` : `${ROBOT_BASE_IMPORT}\n\n${code}`;
}

function getRobotStartBlockText(
  model: MonacoEditor.ITextModel,
  { startLine, endLine }: RobotStartCallRange
): {
  blockText: string;
  insertionLine: number;
} {
  const removalStartLine = getRobotStartRemovalStartLine(model, startLine);
  const lines: string[] = [];
  for (let lineNumber = removalStartLine; lineNumber <= endLine; lineNumber++) {
    lines.push(model.getLineContent(lineNumber));
  }

  return {
    blockText: lines.join('\n'),
    insertionLine: removalStartLine,
  };
}

export function restoreManagedRobotStartCallInCode(previousCode: string, code: string): string | null {
  let nextCode = code;
  let changed = false;

  const nextModel = getLineModelFromCode(nextCode) as MonacoEditor.ITextModel;
  if (findManagedRobotStartCallRanges(nextModel).length === 0) {
    const previousModel = getLineModelFromCode(previousCode) as MonacoEditor.ITextModel;
    const previousRanges = findManagedRobotStartCallRanges(previousModel);
    if (previousRanges.length === 0) return null;

    const lineCount = nextModel.getLineCount();
    const edits = previousRanges
      .map((range) => getRobotStartBlockText(previousModel, range))
      .map(({ blockText, insertionLine }) => {
        const insertLine = Math.max(1, Math.min(insertionLine, lineCount));
        const insertAtEnd = insertionLine > lineCount;
        const insertColumn = insertAtEnd ? nextModel.getLineMaxColumn(insertLine) : 1;
        const lineContent = nextModel.getLineContent(insertLine);
        const prefix = insertAtEnd && lineContent.trimEnd().length > 0 ? '\n' : '';
        const suffix = insertAtEnd ? '' : '\n';

        return {
          range: new Range(insertLine, insertColumn, insertLine, insertColumn),
          text: `${prefix}${blockText}${suffix}`,
          forceMoveMarkers: true,
        };
      });

    nextCode = applyCodeEdits(nextCode, edits);
    changed = true;
  }

  const codeWithImport = ensureRobotBaseImportInCode(nextCode);
  if (codeWithImport !== nextCode) {
    nextCode = codeWithImport;
    changed = true;
  }

  return changed ? nextCode : null;
}

export function allowNextRobotStartRemoval(model: MonacoEditor.ITextModel) {
  robotStartRemovalAllowance.add(model);
}

export function consumeRobotStartRemovalAllowance(model: MonacoEditor.ITextModel): boolean {
  if (!robotStartRemovalAllowance.has(model)) return false;
  robotStartRemovalAllowance.delete(model);
  return true;
}

function getRobotStartRemovalStartLine(model: MonacoEditor.ITextModel, startLine: number): number {
  if (startLine <= 1) return startLine;
  return model.getLineContent(startLine - 1).trim() === ROBOT_START_COMMENT ? startLine - 1 : startLine;
}

function getRobotStartRemovalEdits(model: MonacoEditor.ITextModel, ranges: RobotStartCallRange[]): RobotStartEdit[] {
  return ranges
    .slice()
    .sort((left, right) => right.startLine - left.startLine)
    .map(({ startLine, endLine }) => {
      const removalStartLine = getRobotStartRemovalStartLine(model, startLine);
      const isLastLine = endLine >= model.getLineCount();
      return {
        range: isLastLine ? new Range(removalStartLine, 1, endLine, model.getLineMaxColumn(endLine)) : new Range(removalStartLine, 1, endLine + 1, 1),
        text: '',
        forceMoveMarkers: true,
      };
    });
}

function getRobotStartInsertEdit(model: MonacoEditor.ITextModel): RobotStartEdit {
  const lineCount = model.getLineCount();
  const lastLine = lineCount >= 1 ? model.getLineContent(lineCount) : '';
  const robotStartBlock = `${ROBOT_START_COMMENT}\nRobotBase().start()\n`;
  const insertText = lastLine.trimEnd().length === 0 ? robotStartBlock : `\n${robotStartBlock}`;
  const insertLine = lineCount >= 1 ? lineCount : 1;
  const insertColumn = lineCount >= 1 ? model.getLineMaxColumn(lineCount) : 1;

  return { range: new Range(insertLine, insertColumn, insertLine, insertColumn), text: insertText, forceMoveMarkers: true };
}

function getFullModelReplaceEdit(model: MonacoEditor.ITextModel, text: string): RobotStartEdit {
  return {
    range: new Range(1, 1, model.getLineCount(), model.getLineMaxColumn(model.getLineCount())),
    text,
    forceMoveMarkers: true,
  };
}

export function removeRobotStartCalls(
  editor: MonacoEditor.IStandaloneCodeEditor,
  editSource = 'robot.start.remove',
  options?: { allowCommandManagementRemoval?: boolean }
): boolean {
  const model = editor.getModel();
  if (!model) return false;

  const ranges = findManagedRobotStartCallRanges(model);
  if (ranges.length === 0) return false;
  if (options?.allowCommandManagementRemoval) allowNextRobotStartRemoval(model);

  const edits = getRobotStartRemovalEdits(model, ranges);

  editor.pushUndoStop();
  editor.executeEdits(editSource, edits);
  editor.pushUndoStop();
  return true;
}

export function removeRobotStartCallsFromCode(code: string): string | null {
  const model = getLineModelFromCode(code) as MonacoEditor.ITextModel;
  const ranges = findManagedRobotStartCallRanges(model);
  if (ranges.length === 0) return null;

  return applyCodeEdits(code, getRobotStartRemovalEdits(model, ranges));
}

export function ensureRobotStartCall(editor: MonacoEditor.IStandaloneCodeEditor, editSource = 'robot.start.ensure'): boolean {
  const model = editor.getModel();
  if (!model) return false;

  const nextCode = ensureRobotStartCallInCode(getCodeTextFromModel(model));
  if (nextCode === null) return false;

  editor.pushUndoStop();
  editor.executeEdits(editSource, [getFullModelReplaceEdit(model, nextCode)]);
  editor.pushUndoStop();
  return true;
}

export function ensureRobotStartCallInCode(code: string): string | null {
  const model = getLineModelFromCode(code) as MonacoEditor.ITextModel;
  let nextCode = code;
  let changed = false;

  if (findManagedRobotStartCallRanges(model).length === 0) {
    nextCode = applyCodeEdits(nextCode, [getRobotStartInsertEdit(model)]);
    changed = true;
  }

  const codeWithImport = ensureRobotBaseImportInCode(nextCode);
  if (codeWithImport !== nextCode) {
    nextCode = codeWithImport;
    changed = true;
  }

  return changed ? nextCode : null;
}

/** Returns 1-based insert line before start(), keeping comment above start attached to it. */
export function getInsertLineBeforeRobotStart(model: MonacoEditor.ITextModel): number | null {
  return getInsertLineBeforeRobotStartInCode(getCodeTextFromModel(model));
}

/** Last 1-based line to scan for existing mapping anchors before `robot.start()`. */
export function getLastMappingScanLineBeforeRobotStart(model: MonacoEditor.ITextModel): number {
  const startLine = findRobotStartLineInCode(getCodeTextFromModel(model));
  return startLine === null ? model.getLineCount() : startLine - 1;
}

export function clampInsertLineBeforeRobotStart(model: MonacoEditor.ITextModel, insertLine: number): number {
  const insertBeforeStart = getInsertLineBeforeRobotStart(model);
  return insertBeforeStart === null ? insertLine : Math.min(insertLine, insertBeforeStart);
}

function getModelLines(model: MonacoEditor.ITextModel): string[] {
  const lines: string[] = [];
  for (let i = 1; i <= model.getLineCount(); i++) {
    lines.push(model.getLineContent(i));
  }
  return lines;
}

function findExistingRegionBlockBounds(
  model: MonacoEditor.ITextModel,
  startMarker: string,
  endMarker: string
): { startLine: number; endLine: number } | null {
  const startMatches = model.findMatches(`^\\s*${escapeRegex(startMarker)}\\s*$`, false, true, true, null, true);
  if (startMatches.length === 0) return null;

  if (endMarker === REGION_END) {
    const lines = getModelLines(model);
    for (const match of startMatches) {
      const startLine = match.range.startLineNumber;
      const endLineIndex = findRegionEndLineIndexAfterStart(lines, startLine - 1);
      if (endLineIndex !== null) return { startLine, endLine: endLineIndex + 1 };
    }
    return null;
  }

  const endMatches = model.findMatches(`^\\s*${escapeRegex(endMarker)}\\s*$`, false, true, true, null, true);
  for (const match of startMatches) {
    const startLine = match.range.startLineNumber;
    const endLine = endMatches.find((endMatch) => endMatch.range.startLineNumber > startLine)?.range.startLineNumber;
    if (endLine !== undefined) return { startLine, endLine };
  }
  return null;
}

export function findInputMappingsRegionBlockBounds(model: MonacoEditor.ITextModel): { startLine: number; endLine: number } | null {
  return findExistingRegionBlockBounds(model, INPUT_MAPPINGS_REGION_START, REGION_END);
}

export function findOrCreateRegionBlock(
  editor: MonacoEditor.IStandaloneCodeEditor,
  opts: { startMarker: string; endMarker: string; insertLine: number; editSource?: string }
): { startLine: number; endLine: number } {
  const model = editor.getModel();
  if (!model) return { startLine: 1, endLine: 1 };

  const existing = findExistingRegionBlockBounds(model, opts.startMarker, opts.endMarker);
  if (existing) return existing;

  const text = `${opts.startMarker}\n${opts.endMarker}\n\n`;
  editor.pushUndoStop();
  editor.executeEdits(opts.editSource ?? 'mappings.block', [
    { range: new Range(opts.insertLine, 1, opts.insertLine, 1), text, forceMoveMarkers: true },
  ]);
  editor.pushUndoStop();

  const created = findExistingRegionBlockBounds(model, opts.startMarker, opts.endMarker);
  if (created) return created;

  return { startLine: opts.insertLine, endLine: opts.insertLine + 1 };
}

export function getRemoveEmptyInputMappingsRegionEdit(model: MonacoEditor.ITextModel): MonacoEditor.IIdentifiedSingleEditOperation | null {
  const lines = getModelLines(model);
  const span = findRegionSpanLineIndices(lines, isInputMappingsRegionStartLine);
  if (!span || !isRegionInteriorEmpty(lines, span)) return null;

  const startLineNumber = span.startLine + 1;
  const endLineNumber = span.endLine + 1;
  return {
    range: new Range(startLineNumber, 1, endLineNumber + 1, 1),
    text: '',
    forceMoveMarkers: true,
  };
}

export function removeEmptyInputMappingsRegionIfNeeded(editor: MonacoEditor.IStandaloneCodeEditor): boolean {
  const model = editor.getModel();
  if (!model) return false;
  const edit = getRemoveEmptyInputMappingsRegionEdit(model);
  if (!edit) return false;
  editor.executeEdits('inputMappings.region.cleanup', [edit]);
  return true;
}
